import { useEffect, useState } from 'react'
import { NodeProps } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { NodeStep, StepType } from '@ui/__generated__/graphql'
import { CloseIcon } from '@ui/components/icons'
import { uuid } from '@ui/lib/utils'
import { GetNodeStepsQuery } from '@ui/loaders/workflow'
import { useLayoutPanelManagerStore, useNodeAndEdgeStore, useNodeStepStore } from '@ui/stores/pipelinesWorkflow'

export const CreateStep = ({ node, onAddStep }: { node: NodeProps; onAddStep: (step: NodeStep) => void }) => {
  const { data: fetchedStepTypes } = GetNodeStepsQuery()
  const [stepTypes, setStepTypes] = useState<StepType[]>([])

  const { setNodes, setRefreshLayout } = useNodeAndEdgeStore(
    useShallow((state) => ({
      setNodes: state.setNodes,
      setRefreshLayout: state.setRefreshLayout,
    }))
  )

  const { setModalContent } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      setModalContent: state.setModalContent,
    }))
  )

  const { setConfig } = useNodeStepStore(
    useShallow((state) => ({
      setConfig: state.setConfig,
    }))
  )

  useEffect(() => {
    if (fetchedStepTypes) {
      setStepTypes(fetchedStepTypes.stepTypes)
    }
  }, [fetchedStepTypes])

  const addStepToNode = (step: StepType) => {
    const newStepId = uuid()

    const newStep: NodeStep = {
      id: newStepId,
      nodeId: node.id,
      stepType: step,
      order: node.data.steps.length,
      logsEnabled: step.logsEnabled,
      spansEnabled: step.spansEnabled,
      metricsEnabled: step.metricsEnabled,
      config: {},
      predicate: {},
    }
    const newSteps = [...node.data.steps, newStep]
    setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, steps: newSteps } } : n)))
    setRefreshLayout(true)
    // We need to set the config for the new step so that the step panel is initialized with the correct default inputs
    setConfig(newStepId, newStep.stepType.type, {})
    setModalContent({ content: null, open: false })
    onAddStep(newStep)
  }

  return (
    <>
      <div className="flex w-full justify-between border-b border-gray-200 p-4">
        <p className="text-base font-medium">Add new step</p>
        <CloseIcon
          className="h-3.5 w-3.5 cursor-pointer  stroke-gray-500 hover:stroke-gray-700"
          onClick={() => setModalContent({ content: null, open: false })}
        />
      </div>
      <div className="flex flex-col space-y-2 p-4">
        {stepTypes.map((step) => (
          <div
            key={step.id}
            onClick={() => addStepToNode(step)}
            className="flex w-full cursor-pointer flex-col justify-between space-y-2 rounded p-2 hover:bg-gray-100">
            <p className="text-xs font-semibold">
              {step.type.toLocaleLowerCase().slice(0, 1).toUpperCase() + step.type.toLocaleLowerCase().slice(1)}
            </p>
            <p className="text-xs font-normal">{step.description}</p>
            <div className="flex flex-row space-x-2">
              {/* {step.logsEnabled && (
                <Chip>
                  <p className="text-xs font-medium">Logs</p>
                </Chip>
              )}
              {step.spansEnabled && (
                <Chip>
                  <p className="text-xs font-medium">Spans</p>
                </Chip>
              )} */}
              {/* {step.metricsEnabled && (
                <Chip>
                  <p className="text-xs font-medium">Metrics</p>
                </Chip>
              )} */}
            </div>
          </div>
        ))}

        <div className="rounded-md bg-gray-50 p-4">
          <p className="text-xs font-normal text-gray-600">
            <span className="font-semibold">Important Note:</span> The order of these steps affects the final output, so
            be mindful of the sequence in which steps are applied. No more than ten steps can be applied to a
            transformation.
          </p>
        </div>
      </div>
    </>
  )
}
